import { Server as SocketIOServer } from "socket.io";
import type { Server as HttpServer } from "http";
import { verifyToken } from "@clerk/express";
import { db } from "@workspace/db";
import { tendersTable, usersTable } from "@workspace/db";
import { and, eq } from "drizzle-orm";
import { logger } from "./logger";

let io: SocketIOServer | null = null;

export interface PresenceViewer {
  userId: number;
  userName: string;
}

// tenderId → Map<socketId, PresenceViewer>
const tenderRosters = new Map<number, Map<string, PresenceViewer>>();

function addToRoster(tenderId: number, socketId: string, viewer: PresenceViewer): void {
  if (!tenderRosters.has(tenderId)) tenderRosters.set(tenderId, new Map());
  tenderRosters.get(tenderId)!.set(socketId, viewer);
}

function removeFromRoster(tenderId: number, socketId: string): void {
  const roster = tenderRosters.get(tenderId);
  if (!roster) return;
  roster.delete(socketId);
  if (roster.size === 0) tenderRosters.delete(tenderId);
}

function broadcastPresence(tenderId: number): void {
  if (!io) return;
  const roster = tenderRosters.get(tenderId);
  const viewers: PresenceViewer[] = roster ? Array.from(roster.values()) : [];
  io.to(`tender:${tenderId}`).emit("presence:update", { tenderId, viewers });
}

export function initSocketIO(httpServer: HttpServer): SocketIOServer {
  io = new SocketIOServer(httpServer, {
    cors: { origin: true, credentials: true },
    path: "/api/socket.io",
  });

  io.use(async (socket, next) => {
    try {
      const token =
        (socket.handshake.auth as Record<string, string>)?.token ||
        (socket.handshake.headers["authorization"] ?? "").replace("Bearer ", "");

      if (!token) {
        return next(new Error("Authentication required"));
      }

      const payload = await verifyToken(token, {
        secretKey: process.env["CLERK_SECRET_KEY"] ?? "",
      });

      if (!payload?.sub) return next(new Error("Invalid token"));

      const user = await db
        .select({
          id: usersTable.id,
          companyId: usersTable.companyId,
          firstName: usersTable.firstName,
          lastName: usersTable.lastName,
          email: usersTable.email,
        })
        .from(usersTable)
        .where(eq(usersTable.clerkId, payload.sub))
        .limit(1);

      if (!user[0]) return next(new Error("User not found"));

      const userName =
        [user[0].firstName, user[0].lastName].filter(Boolean).join(" ").trim() ||
        user[0].email;

      (socket.data as Record<string, unknown>).userId = user[0].id;
      (socket.data as Record<string, unknown>).companyId = user[0].companyId;
      (socket.data as Record<string, unknown>).userName = userName;
      (socket.data as Record<string, unknown>).joinedTenders = new Set<number>();
      next();
    } catch (err) {
      logger.warn({ err }, "Socket auth failed");
      next(new Error("Authentication failed"));
    }
  });

  io.on("connection", (socket) => {
    const companyId = (socket.data as Record<string, unknown>).companyId as number | null;
    const userId = (socket.data as Record<string, unknown>).userId as number;
    const userName = (socket.data as Record<string, unknown>).userName as string;
    const joinedTenders = (socket.data as Record<string, unknown>).joinedTenders as Set<number>;

    logger.info({ socketId: socket.id, companyId }, "Socket connected");

    socket.on("join:tender", async (tenderId: unknown) => {
      try {
        const tid = Number(tenderId);
        if (!tid || !companyId) return;

        const tender = await db
          .select({ id: tendersTable.id })
          .from(tendersTable)
          .where(and(eq(tendersTable.id, tid), eq(tendersTable.companyId, companyId)))
          .limit(1);

        if (!tender[0]) {
          logger.warn({ socketId: socket.id, tenderId: tid }, "Socket join denied – tender not owned by company");
          return;
        }

        const room = `tender:${tid}`;
        socket.join(room);
        joinedTenders.add(tid);
        addToRoster(tid, socket.id, { userId, userName });
        broadcastPresence(tid);
        logger.info({ socketId: socket.id, room }, "Socket joined tender room");
      } catch (err) {
        logger.error({ err }, "Socket join:tender error");
      }
    });

    socket.on("leave:tender", (tenderId: unknown) => {
      const tid = Number(tenderId);
      if (!tid) return;
      socket.leave(`tender:${tid}`);
      joinedTenders.delete(tid);
      removeFromRoster(tid, socket.id);
      broadcastPresence(tid);
    });

    socket.on("disconnect", () => {
      for (const tid of joinedTenders) {
        removeFromRoster(tid, socket.id);
        broadcastPresence(tid);
      }
      logger.info({ socketId: socket.id }, "Socket disconnected");
    });
  });

  logger.info("Socket.IO server initialized");
  return io;
}

export function broadcastTenderUpdate(tenderId: number, event: string, payload: unknown): void {
  if (!io) return;
  io.to(`tender:${tenderId}`).emit(event, payload);
}

export function broadcastToUser(userId: number, event: string, payload: unknown): void {
  if (!io) return;
  io.to(`user:${userId}`).emit(event, payload);
}

export function getIO(): SocketIOServer | null {
  return io;
}
